import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column } from 'react-table';

import { ORG_DELETION } from '../../../../shared/featureFlags/flagNames';
import { ORG_DELETION_STATUSES } from '../../../../shared/InternalTools/orgDeletion';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { useGetOrgDeletions } from '../../../hooks/api/internalTools/orgDeletion/useGetOrgDeletions';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import Accordion from '../../generic/Accordion';
import Card from '../../generic/Card/Card';
import Flex from '../../generic/Flex';
import LabeledSelect from '../../generic/Forms/LabeledSelect';
import TextInput from '../../generic/Forms/TextInput';
import Loader from '../../generic/Loader';
import { DEFAULT_PAGE_SIZE } from '../../generic/Pagination/consts';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import SimpleReactTable from '../../generic/SimpleReactTable';
import TableHeader from '../../generic/TableHeader';
import type { OrgDeletionStatus } from '../../../../shared/InternalTools/orgDeletion';
import type { ListOrganizationDeletionsRequest } from '../../../../shared/types/routes/internalTools/orgDeletion';

import {
  DateCell,
  ORG_DELETION_STATUS_LABELS,
  OrganizationCell,
  StatusCell,
  TextCell,
} from './OrgDeletionCells';

type Props = {
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
};

type StatusOption = {
  value: OrgDeletionStatus | null;
  label: string;
};

const STATUS_OPTIONS: StatusOption[] = [
  { value: null, label: 'All statuses' },
  ...ORG_DELETION_STATUSES.map((status) => ({ value: status, label: ORG_DELETION_STATUS_LABELS[status] })),
];

const column = (id: string, title: string, extra: Partial<Column>): Partial<Column> => ({
  id,
  sortable: false,
  Header: <TableHeader title={title} />,
  resizable: true,
  style: { height: 'auto', whiteSpace: 'normal' },
  ...extra,
});

// Server order (newest scheduled first) is kept, so no column sorts.
const COLUMNS = [
  column('organization', 'Organization', { accessor: 'organizationId', Cell: OrganizationCell }),
  column('status', 'Status', { accessor: 'status', Cell: StatusCell, width: 120 }),
  column('scheduledFor', 'Scheduled For', { accessor: 'scheduledFor', Cell: DateCell }),
  column('requesterEmail', 'Requested By', { accessor: 'requesterEmail' }),
  column('startedAt', 'Started At', { accessor: 'startedAt', Cell: DateCell }),
  column('cancelledByEmail', 'Cancelled By', { accessor: 'cancelledByEmail', Cell: TextCell }),
  column('cancelledAt', 'Cancelled At', { accessor: 'cancelledAt', Cell: DateCell }),
  column('completedAt', 'Completed At', { accessor: 'completedAt', Cell: DateCell }),
  column('failedAt', 'Failed At', { accessor: 'failedAt', Cell: DateCell }),
  column('failureReason', 'Failure Reason', { accessor: 'failureReason', Cell: TextCell }),
];

/**
 * Every org deletion, past and upcoming, with search, filters and paging. The page shows it only with the org
 * deletion flag on.
 */
export const OrgDeletionsCard: VoidFunctionComponent<Props> = ({ isOpen, onToggle }) => {
  const [isSectionOpen, setIsSectionOpen] = useState(isOpen ?? false);
  const handleToggle = () => {
    setIsSectionOpen(!isSectionOpen);
    onToggle?.(isSectionOpen);
  };

  const isFlagOn = useIsFeatureEnabled(ORG_DELETION);
  const [search, setSearch] = useState('');
  const [requesterEmail, setRequesterEmail] = useState('');
  const [status, setStatus] = useState<OrgDeletionStatus | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // One request per pause in typing, not per keystroke.
  const { debouncedValue: debouncedSearch } = useDebouncedValue(search, 500);
  const { debouncedValue: debouncedEmail } = useDebouncedValue(requesterEmail, 500);
  const trimmedSearch = debouncedSearch.trim();
  const trimmedEmail = debouncedEmail.trim();

  // Empty filters are left out, so the server applies none.
  const params: ListOrganizationDeletionsRequest = {
    page,
    pageSize,
    ...(trimmedSearch && { search: trimmedSearch }),
    ...(trimmedEmail && { requesterEmail: trimmedEmail }),
    ...(status && { status }),
  };
  const { data, isLoading, error } = useGetOrgDeletions(params, isFlagOn);

  const changeFilter = <T,>(set: (value: T) => void) => (value: T) => {
    set(value);
    setPage(1);
  };

  const renderList = () => {
    if (isLoading) {
      return <Loader />;
    }
    if (error) {
      return <>Failed to load organization deletions</>;
    }
    const results = data?.results ?? [];
    const totalCount = data?.totalCount ?? 0;
    // The footer stays while anything matches, so a later page that empties between refetches can still be left.
    return (
      <>
        {results.length === 0 ? (
          <>No organization deletions match these filters.</>
        ) : (
          <SimpleReactTable
            data={results}
            columns={COLUMNS}
            isSortable={false}
            pageSize={pageSize}
            minRows={1}
          />
        )}
        {totalCount > 0 && (
          <div className="margin-top-md">
            <PaginationFooter
              page={page}
              count={pageSize}
              totalItems={totalCount}
              setPage={setPage}
              setCount={changeFilter(setPageSize)}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <Card isFullWidth padding="medium">
      <Card.Content>
        <Accordion id="org-deletions" isInitiallyOpen={isSectionOpen}>
          <Accordion.Target onClick={handleToggle}>Organization Deletions</Accordion.Target>
          <Accordion.Body>
            <Flex gap={12} align="flex-end" className="margin-bottom-md">
              <TextInput
                id="org-deletions-search"
                label="Search by organization ID or title"
                placeholder="Organization ID or title"
                value={search}
                maxLength={255}
                onChange={changeFilter(setSearch)}
              />
              <TextInput
                id="org-deletions-requester-email"
                label="Requester email"
                placeholder="Full email address"
                value={requesterEmail}
                maxLength={255}
                onChange={changeFilter(setRequesterEmail)}
              />
              <LabeledSelect
                id="org-deletions-status"
                label="Status"
                options={STATUS_OPTIONS}
                value={STATUS_OPTIONS.find((option) => option.value === status)}
                onChange={(option) => changeFilter(setStatus)(option?.value ?? null)}
              />
            </Flex>
            {renderList()}
          </Accordion.Body>
        </Accordion>
      </Card.Content>
    </Card>
  );
};
